import { Edit, Delete, NorthEast, SouthWest } from "@mui/icons-material";

import { TableActions } from "../../components/v3";

/**
 * Route paths for the project module, mounted under `/projects`.
 * Single source of truth — reuse these instead of hard-coding `/projects...` strings.
 * The child paths (other than `root`) are relative to the `/projects` wrapper route.
 */
export const PROJECT_ROUTES = {
  root: "/projects",
  details: ":id",
  add: "add",
  edit: "edit/:id",
  stages: ":id/stages",
  tasks: ":id/tasks",
  invoices: ":id/invoices",
  performanceGuarantees: ":id/performance-guarantees",
} as const;

export const ProjectTableColumns = [
  { field: "sl", headerName: "Sl No", flex: 0.8, sortable: false },
  {
    field: "projectName",
    headerName: "Project Name",
    flex: 2,
    sortable: false,
  },
  {
    field: "customerName",
    headerName: "Client Name",
    flex: 2,
    sortable: false,
  },
  {
    field: "estimatedAmount",
    headerName: "Estimated Amount",
    flex: 1.5,
    sortable: false,
  },
  {
    field: "clientBalance",
    headerName: "Client Balance",
    flex: 1.5,
    sortable: false,
    renderCell: (params) => {
      return `₹ ${Number(params.value).toFixed(2)}`;
    },
  },
  {
    field: "totalExpense",
    headerName: "Total Expense",
    flex: 1.5,
    sortable: false,
    renderCell: (params) => {
      const value = params.value;
      let color = "#5e5d72";

      if (value > 0) color = "red";
      else if (value < 0) color = "green";
      if (!value) return "-";
      return (
        <span style={{ color: color, display: "flex", alignItems: "center", gap: 4 }}>
          <NorthEast fontSize="small" />₹ {Number(params.value).toFixed(2)}
        </span>
      );
    },
  },
  {
    field: "totalIncome",
    headerName: "Total Income",
    flex: 1.5,
    sortable: false,
    renderCell: (params) => {
      const value = params.value;
      let color = "#5e5d72";

      if (value > 0) color = "green";
      else if (value < 0) color = "red";
      if (!value) return "-";
      return (
        <span style={{ color: color, display: "flex", alignItems: "center", gap: 4 }}>
          <SouthWest fontSize="small" />₹ {Number(params.value).toFixed(2)}
        </span>
      );
    },
  },
  {
    field: "profit",
    headerName: "Profit",
    flex: 1.5,
    sortable: false,
    renderCell: (params) => {
      const value = Number(params.value);
      let color = "#5e5d72";

      if (value > 0) color = "green";
      else if (value < 0) color = "red";

      return <span style={{ color, fontWeight: 500 }}>₹ {Number(value)}</span>;
    },
  },
];

export const ProjectIncomeTableColumns = (handleActions, canEditIncome, canDeleteIncome) => {
  const headers = [
    {
      field: "sl",
      headerName: "Sl No",
      width: 80,
      sortable: false,
    },
    { field: "date", headerName: "Date", flex: 0.5, sortable: true, sortingOrder: ["desc", "asc", null] },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      sortable: true,
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      sortable: true,
    },
  ];

  if (canEditIncome || canDeleteIncome) {
    headers.push({
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => {
        const actions = [];
        if (canEditIncome) {
          actions.push({
            icon: Edit,
            type: "edit",
            label: "Edit",
            onClick: handleActions,
          });
        }
        if (canDeleteIncome) {
          actions.push({
            icon: Delete,
            type: "delete",
            label: "Delete",
            onClick: handleActions,
          });
        }
        return <TableActions item={params} actions={actions} />;
      },
    });
  }

  return headers;
};

export const ProjectWorkTableColumns = (handleActions, canEditWork, canDeleteWork) => {
  const headers = [
    {
      field: "sl",
      headerName: "Sl No",
      width: 80,
      sortable: false,
    },
    { field: "date", headerName: "Date", flex: 1, sortable: true, sortingOrder: ["desc", "asc", null] },
    {
      field: "type",
      headerName: "Type",
      flex: 0.7,
      sortable: false,
    },
    {
      field: "workerName",
      headerName: "Worker",
      flex: 1.5,
      sortable: false,
    },
    {
      field: "wage",
      headerName: "Wage",
      flex: 1,
      sortable: true,
    },
    {
      field: "overTime",
      headerName: "Over Time",
      flex: 1,
      sortable: false,
    },
    {
      field: "total",
      headerName: "Total",
      flex: 1,
      sortable: false,
    },
  ];

  if (canEditWork || canDeleteWork) {
    headers.push({
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => {
        const actions = [];
        if (canEditWork) {
          actions.push({
            icon: Edit,
            type: "edit",
            label: "Edit",
            onClick: handleActions,
          });
        }
        if (canDeleteWork) {
          actions.push({
            icon: Delete,
            type: "delete",
            label: "Delete",
            onClick: handleActions,
          });
        }
        return <TableActions item={params} actions={actions} />;
      },
    });
  }

  return headers;
};

export const ProjectPurchaseTableColumns = (handleActions, canEditPurchase, canDeletePurchase) => {
  const headers = [
    {
      field: "sl",
      headerName: "Sl No",
      width: 80,
      sortable: false,
    },
    { field: "date", headerName: "Date", flex: 0.5, sortable: true, sortingOrder: ["desc", "asc", null] },
    { field: "type", headerName: "Type", flex: 1, sortable: false },
    {
      field: "vendorName",
      headerName: "Vendor",
      flex: 1.5,
      sortable: false,
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const value = params.value;
        const { type } = params.row;
        if (!value) return "-";
        const color = type === "Return" ? "black" : "red";
        return <span style={{ color, display: "flex", alignItems: "center", gap: 4 }}>₹ {Number(value).toFixed(2)}</span>;
      },
    },
  ];

  if (canEditPurchase || canDeletePurchase) {
    headers.push({
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => {
        const actions = [];
        if (canEditPurchase) {
          actions.push({
            icon: Edit,
            type: "edit",
            label: "Edit",
            onClick: handleActions,
          });
        }
        if (canDeletePurchase) {
          actions.push({
            icon: Delete,
            type: "delete",
            label: "Delete",
            onClick: handleActions,
          });
        }
        return <TableActions item={params} actions={actions} />;
      },
    });
  }

  return headers;
};

export const ProjectContractTableColumns = (handleActions, canEditContract, canDeleteContract) => {
  const headers = [
    {
      field: "sl",
      headerName: "Sl No",
      width: 80,
      sortable: false,
    },
    { field: "date", headerName: "Date", flex: 0.5, sortable: true, sortingOrder: ["desc", "asc", null] },
    { field: "subcontractor", headerName: "Subcontractor", flex: 1, sortable: false },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const value = params.value;
        const { type } = params.row;
        const color = type === "expense" ? "green" : "red";
        if (!value) return "-";

        return <span style={{ color, display: "flex", alignItems: "center", gap: 4 }}>₹ {Number(value).toFixed(2)}</span>;
      },
    },
  ];

  if (canEditContract || canDeleteContract) {
    headers.push({
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => {
        const actions = [];
        if (canEditContract) {
          actions.push({
            icon: Edit,
            type: "edit",
            label: "Edit",
            onClick: handleActions,
          });
        }
        if (canDeleteContract) {
          actions.push({
            icon: Delete,
            type: "delete",
            label: "Delete",
            onClick: handleActions,
          });
        }
        return <TableActions item={params} actions={actions} />;
      },
    });
  }
  return headers;
};

export const ProjectToolColumns = [
  {
    field: "sl",
    headerName: "Sl No",
    width: 80,
    sortable: false,
  },
  { field: "updatedAt", headerName: "Date", flex: 0.5, sortable: true, sortingOrder: ["desc", "asc", null] },
  {
    field: "tool",
    headerName: "Tool",
    flex: 1.5,
    sortable: false,
  },
  {
    field: "quantity",
    headerName: "Quantity",
    flex: 1,
    sortable: true,
  },
];

export const getStageTableColumns = (handleActions, hasEditStage, hasDeleteStage) => {
  const headers = [
    { field: "sl", headerName: "Sl No", flex: 0.75, sortable: false },
    { field: "stageId", headerName: "stage", flex: 1.5, sortable: false },
    { field: "estimatedAmount", headerName: "Estimated Amount", flex: 1.5, sortable: false },
    { field: "expense", headerName: "Expense", flex: 1.5, sortable: false },
    { field: "income", headerName: "Income", flex: 1.5, sortable: false },
    { field: "profit", headerName: "Profit", flex: 1.5, sortable: false },
    { field: "balance", headerName: "Balance", flex: 1.5, sortable: false },
  ];

  if (hasEditStage || hasDeleteStage) {
    headers.push({
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const actions = [];
        if (hasEditStage) {
          actions.push({
            icon: Edit,
            type: "edit",
            label: "Edit",
            onClick: handleActions,
          });
        }
        if (hasDeleteStage) {
          actions.push({
            icon: Delete,
            type: "delete",
            label: "Delete",
            onClick: handleActions,
          });
        }
        return <TableActions item={params} actions={actions} />;
      },
    });
  }

  return headers;
};

export const getInvoiceTableColumns = (handleActions, hasEditInvoice, hasDeleteInvoice) => {
  const headers = [
    { field: "sl", headerName: "Sl No", flex: 0.75, sortable: false },
    { field: "invoiceNumber", headerName: "Invoice Number", flex: 1.5, sortable: false },
    { field: "date", headerName: "Date", flex: 1.5, sortable: false },
    { field: "amount", headerName: "Amount", flex: 1.5, sortable: false },
  ];

  if (hasEditInvoice || hasDeleteInvoice) {
    headers.push({
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const actions = [];

        if (hasEditInvoice) {
          actions.push({
            icon: Edit,
            type: "edit",
            label: "Edit",
            onClick: handleActions,
          });
        }

        if (hasDeleteInvoice) {
          actions.push({
            icon: Delete,
            type: "delete",
            label: "Delete",
            onClick: handleActions,
          });
        }

        return <TableActions item={params} actions={actions} />;
      },
    });
  }

  return headers;
};
